import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { TextDateInputDelayedSubmission } from "../../../../components/ui/text-date-input";
import { Textarea } from "../../../../components/ui/textarea";
import { useRemindersStore } from "../../../../store/Reminders";

interface PauseRemindersDialogProps {
	open: boolean;
	invoiceIds: number[];
	onClose: () => void;
	onCompleted: () => void;
}

export const PauseRemindersDialog: React.FC<PauseRemindersDialogProps> = ({
	open,
	invoiceIds,
	onClose,
	onCompleted,
}) => {
	const pauseReminders = useRemindersStore((state) => state.pauseReminders);
	const [until, setUntil] = useState<string | null>(null);
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	const handleClose = () => {
		setUntil(null);
		setReason("");
		onClose();
	};

	const handleConfirm = async () => {
		setSaving(true);
		const success = await pauseReminders(invoiceIds, until, reason.trim());
		setSaving(false);
		if (!success) return;
		onCompleted();
		handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
			<DialogContent
				className="sm:max-w-md"
				data-testid="pause-reminders-dialog"
				aria-label="Mahnstopp setzen"
			>
				<DialogHeader>
					<DialogTitle>Mahnstopp setzen</DialogTitle>
					<DialogDescription>
						{invoiceIds.length === 1
							? "Die Rechnung wird bis zum gewählten Datum nicht gemahnt."
							: `${invoiceIds.length} Rechnungen werden bis zum gewählten Datum nicht gemahnt.`}{" "}
						Ohne Datum gilt der Mahnstopp unbefristet.
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label htmlFor="reminder-pause-until">
							Mahnstopp bis
						</Label>
						<TextDateInputDelayedSubmission
							id="reminder-pause-until"
							value={until ?? ""}
							onDateChange={(date) => setUntil(date || null)}
							placeholder="TT.MM.JJJJ"
							aria-label="Mahnstopp bis"
							data-testid="reminder-pause-until-input"
						/>
					</div>
					<div className="flex flex-col gap-2">
						<Label htmlFor="reminder-pause-reason">
							Grund <span className="text-red-500">*</span>
						</Label>
						<Textarea
							id="reminder-pause-reason"
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							placeholder="z.B. Ratenzahlung vereinbart"
							rows={3}
							className="resize-none"
							aria-label="Grund für den Mahnstopp"
							data-testid="reminder-pause-reason-input"
						/>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={handleClose}
						aria-label="Abbrechen"
						data-testid="cancel-pause-reminders-button"
					>
						Abbrechen
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!reason.trim() || saving}
						aria-label="Mahnstopp speichern"
						data-testid="confirm-pause-reminders-button"
					>
						Mahnstopp setzen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
